import React, { useMemo, useCallback, useEffect, useRef, useState } from 'react';
import type { ConfiguredService } from '../../../types/service.types';
import type { ConfiguredAddOn } from '../../../types/addon.types';
import type { Service } from '../../../types/services.types';
import { useDerivedSchedule, derivedScheduleQueryKey } from '../../../hooks';
import type { Vehicle } from '@features/vehiclesearch/types/vinvrmsearch.types';
import type { VehicleDerivative } from '@features/vehiclesearch/types/manualsearch.types';
import type { ServiceCountMode } from '../../../utils/resolveServiceCountMode';
import type { DateKey } from '@features/quote/utils/dateKey';
import { mapDerivedServicesToConfigured } from '../../../utils/mapDerivedServices';
import { configuredServicesFingerprint } from '../../../utils/configuredServicesFingerprint';
import { derivationFingerprint, fingerprintPlanMatches } from '../../../utils/derivationFingerprint';
import { remapLinkedAddOns } from '../../../utils/linkedAddOns';
import { deriveVehicleScheduleContext } from '@/app/utils/vehicleScheduleContext';

interface VerifiedPlan {
    identity: string;
    planId: string | null;
}

// Derived-schedule orchestration: the query, resolved-plan-id persistence,
// guided-count reveal state, catalog-pricing merge, and the sync into configuredServices/configuredAddOns.
export function useDerivedServices({
    mode,
    productId,
    requestedCount,
    selectedVehicle,
    isLoading,
    getServiceById,
    setConfiguredServices,
    setResolvedPlanId,
    configuredAddOns,
    startEntryIndex,
    dateOverrides,
    storedFingerprint,
    applyDerivedSchedule,
    clearPaymentSelection,
}: {
    mode: ServiceCountMode;
    productId: string | undefined;
    requestedCount: number | undefined;
    selectedVehicle: Vehicle | VehicleDerivative | null;
    isLoading: boolean;
    getServiceById: (id: string) => Service | undefined;
    setConfiguredServices: React.Dispatch<React.SetStateAction<ConfiguredService[]>>;
    setResolvedPlanId: (planId: string | null) => void;
    configuredAddOns: ConfiguredAddOn[];
    startEntryIndex: number | null;
    dateOverrides: Record<number, DateKey> | undefined;
    storedFingerprint: string | undefined;
    applyDerivedSchedule: (payload: { services: ConfiguredService[]; addOns: ConfiguredAddOn[] }) => void;
    clearPaymentSelection: () => void;
}) {
    const vehicleContext = useMemo(
        () => deriveVehicleScheduleContext(selectedVehicle),
        [selectedVehicle]
    );

    // Everything that can change which plan resolves, so a matcher-only vehicle
    // switch invalidates the verification the dates below depend on.
    const planResolutionIdentity = JSON.stringify(
        derivedScheduleQueryKey({ productId, count: requestedCount, vehicle: selectedVehicle })
    );
    const [verifiedPlan, setVerifiedPlan] = useState<VerifiedPlan | null>(null);

    const planVerified =
        storedFingerprint !== undefined &&
        verifiedPlan !== null &&
        verifiedPlan.identity === planResolutionIdentity &&
        fingerprintPlanMatches(storedFingerprint, verifiedPlan.planId);

    // Withheld until the current context re-resolves the stamped plan, because
    // the row indices the dates key on are meaningless against any other plan.
    const hasSavedDates = !!dateOverrides && Object.keys(dateOverrides).length > 0;
    const requestDateOverrides = hasSavedDates && !planVerified ? undefined : dateOverrides;
    const dateOverridesInRequest = hasSavedDates && planVerified;

    const {
        data: derivedSchedule,
        isLoading: isDerivedQueryLoading,
        isFetching: isDerivedFetching,
        isPending: isDerivedPending,
        isError: isDerivedError,
        error: derivedError,
    } = useDerivedSchedule({
        productId,
        count: requestedCount,
        vehicle: selectedVehicle,
        startEntryIndex,
        dateOverrides: requestDateOverrides,
    });

    // The withheld-dates request only asks which plan resolves; its base-date
    // rows are provisional, so they neither render nor reach quote state.
    const awaitingPlanVerification =
        hasSavedDates && !isDerivedError && verifiedPlan?.identity !== planResolutionIdentity;
    const isDerivedLoading = isDerivedQueryLoading || awaitingPlanVerification;

    // Persist the resolved plan id on the quote. `setResolvedPlanId` is a stable
    // useCallback in QuoteContext, so it is safe in the dependency array.
    const resolvedPlanIdFromSchedule = derivedSchedule?.resolvedPlan?.id ?? null;
    useEffect(() => {
        setResolvedPlanId(resolvedPlanIdFromSchedule);
    }, [resolvedPlanIdFromSchedule, setResolvedPlanId]);

    // Updated a render behind, on purpose: `effectiveResolvedPlanId` below
    // reads the current render's own data directly whenever it has any.
    const lastKnownResolvedPlanIdRef = useRef<string | null>(null);
    useEffect(() => {
        if (derivedSchedule !== undefined) {
            lastKnownResolvedPlanIdRef.current = resolvedPlanIdFromSchedule;
        }
    }, [derivedSchedule, resolvedPlanIdFromSchedule]);

    const effectiveResolvedPlanId =
        derivedSchedule !== undefined ? resolvedPlanIdFromSchedule : lastKnownResolvedPlanIdRef.current;

    useEffect(() => {
        if (derivedSchedule === undefined) return;
        setVerifiedPlan((current) =>
            current?.identity === planResolutionIdentity &&
            current.planId === resolvedPlanIdFromSchedule
                ? current
                : { identity: planResolutionIdentity, planId: resolvedPlanIdFromSchedule }
        );
    }, [derivedSchedule, resolvedPlanIdFromSchedule, planResolutionIdentity]);

    const fingerprint = derivationFingerprint({
        productId: productId ?? null,
        resolvedPlanId: effectiveResolvedPlanId,
        vehicleContext,
    });
    // The start choice selects which entries the rows are, so a positional
    // re-key across a change of it would land add-ons on the wrong service.
    const generation = `${effectiveResolvedPlanId ?? 'none'}:${startEntryIndex ?? 'none'}:${fingerprint}`;

    // The derive's own query identity, not `generation`: D7's fingerprint omits
    // the matcher fields, so a failed identity-only vehicle switch reuses it.
    const requestIdentity = JSON.stringify(
        derivedScheduleQueryKey({
            productId,
            count: requestedCount,
            vehicle: selectedVehicle,
            startEntryIndex,
            dateOverrides: requestDateOverrides,
        })
    );

    // `bounds` is the endpoint's authoritative trio, which may differ from
    // the frontend-read `effectiveBounds` if the catalog changed.
    const authoritativeServices = useMemo(
        () => derivedSchedule?.services ?? [],
        [derivedSchedule?.services]
    );
    const bounds = derivedSchedule?.bounds ?? null;

    // Guided-mode visible count: how many of the already-fetched authoritative
    // entries to show. Starts at `bounds.default`; "Add"/"Remove" reveal or
    // hide successive fetched entries within `[min, max]` — no client-side
    // projection, the entries themselves come from the endpoint. Reset on
    // refetch (a new `authoritativeServices` reference).
    const [guidedCount, setGuidedCount] = useState<number | null>(null);
    useEffect(() => {
        if (mode !== 'guided') return;
        setGuidedCount(bounds?.default ?? null);
    }, [mode, bounds?.default, authoritativeServices]);

    const effectiveGuidedCount = guidedCount ?? bounds?.default ?? authoritativeServices.length;

    const derivedRows = useMemo(() => {
        if (mode === 'guided') {
            return authoritativeServices.slice(0, effectiveGuidedCount);
        }
        // Locked: every entry the endpoint returned (already `bounds.default`).
        return authoritativeServices;
    }, [mode, authoritativeServices, effectiveGuidedCount]);

    const canAddGuided =
        mode === 'guided' &&
        !!bounds &&
        effectiveGuidedCount < bounds.max &&
        effectiveGuidedCount < authoritativeServices.length;
    const canRemoveGuided = mode === 'guided' && !!bounds && effectiveGuidedCount > bounds.min;

    const handleGuidedAdd = useCallback(() => {
        if (!bounds) return;
        setGuidedCount((current) => {
            const currentCount = current ?? bounds.default;
            return Math.min(currentCount + 1, bounds.max, authoritativeServices.length);
        });
        // D14: revealing another row changes the schedule the dealer confirmed a payment against.
        clearPaymentSelection();
    }, [bounds, authoritativeServices.length, clearPaymentSelection]);

    const handleGuidedRemove = useCallback(() => {
        if (!bounds) return;
        setGuidedCount((current) => {
            const currentCount = current ?? bounds.default;
            return Math.max(currentCount - 1, bounds.min);
        });
        // D14: hiding a row changes the schedule the dealer confirmed a payment against.
        clearPaymentSelection();
    }, [bounds, clearPaymentSelection]);

    // Derived rows enriched with catalog pricing. The derived-schedule
    // endpoint returns service_id/name/due_date/interval but no price, so
    // price/gross_price/items are looked up from the loaded catalog
    // (useAvailableServices) by service_id.
    const configuredDerivedRows = useMemo(
        () => mapDerivedServicesToConfigured(derivedRows, getServiceById),
        [derivedRows, getServiceById]
    );

    // Every derived service id must resolve to a catalog entry before the
    // rows carry real pricing. Until then the mapper falls back to price 0,
    // which must never be synced into quote state.
    const derivedPricingResolved =
        derivedRows.length > 0 &&
        derivedRows.every((row) => getServiceById(row.serviceId) !== undefined);

    // The generation and row list of the last successfully-applied sync, for
    // remapLinkedAddOns; the identity drives the isDerivedError keep-vs-clear.
    const lastAppliedGenerationRef = useRef<string | null>(null);
    const lastAppliedRowsRef = useRef<ConfiguredService[]>([]);
    const lastAppliedRequestIdentityRef = useRef<string | null>(null);

    const clearApplied = () => {
        setConfiguredServices([]);
        lastAppliedGenerationRef.current = null;
        lastAppliedRowsRef.current = [];
        lastAppliedRequestIdentityRef.current = null;
    };

    // Keyed on a content fingerprint rather than `derivedRows` or the setter identity:
    // `setConfiguredServices` is rebuilt on every change, so depending on it loops.
    const derivedRowsKey = configuredServicesFingerprint(configuredDerivedRows);
    useEffect(() => {
        // Wait for both the schedule and the catalog to settle; never reconcile
        // quote state mid-load or on a schedule error. `isDerivedLoading` is
        // gated (and listed in the deps) so an empty `derivedRows` during the
        // in-flight window is not mistaken for a legitimately empty schedule.
        if (isDerivedLoading || isLoading) return;

        if (isDerivedError) {
            // The saved dates and the rows they were saved against are one
            // document; clearing the rows while it survives is a lost update.
            if (dateOverridesInRequest) {
                return;
            }
            // A transient failure of the SAME request keeps the dealer's
            // configuration; any product/vehicle/override change clears it.
            if (
                lastAppliedRequestIdentityRef.current !== null &&
                lastAppliedRequestIdentityRef.current === requestIdentity
            ) {
                return;
            }
            clearApplied();
            return;
        }
        // Legitimately empty authoritative schedule — e.g. the backend bounds
        // invariant allows `default_service_count === 0` (0 <= min <= default
        // <= max), yielding zero derived rows. The pricing gate below never
        // fires for an empty set (`derivedRowsKey` stays ''), so reconcile
        // explicitly to an empty array here to clear any previously-synced
        // derived/manual rows rather than leaving them stale in quote state.
        if (derivedRows.length === 0) {
            clearApplied();
            return;
        }
        // Non-empty schedule but the catalog cannot price every derived id
        // (missing catalog entries / catalog failure). Never persist the
        // fallback 0, and — as with the schedule-error path above — clear any
        // stale rows so unpriceable derived services cannot leave a
        // previous-product configuration lingering behind the length gate.
        if (!derivedPricingResolved) {
            clearApplied();
            return;
        }

        const remappedAddOns = remapLinkedAddOns(
            configuredAddOns,
            lastAppliedRowsRef.current,
            configuredDerivedRows,
            lastAppliedGenerationRef.current ?? '',
            generation
        );
        applyDerivedSchedule({ services: configuredDerivedRows, addOns: remappedAddOns });
        lastAppliedGenerationRef.current = generation;
        lastAppliedRowsRef.current = configuredDerivedRows;
        lastAppliedRequestIdentityRef.current = requestIdentity;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        derivedRowsKey,
        derivedRows.length,
        isLoading,
        isDerivedLoading,
        isDerivedError,
        derivedPricingResolved,
        generation,
        requestIdentity,
        dateOverridesInRequest,
    ]);

    return {
        derivedSchedule,
        isDerivedLoading,
        isDerivedFetching,
        isDerivedPending,
        isDerivedError,
        derivedError,
        bounds,
        derivedRows,
        configuredDerivedRows,
        derivedPricingResolved,
        canAddGuided,
        canRemoveGuided,
        handleGuidedAdd,
        handleGuidedRemove,
        fingerprint,
        dateOverridesInRequest,
    };
}
